// Floating Text System - Damage numbers, healing, XP gains, etc.
// OPTIMIZED: Object pooling, reduced allocations, batch DOM updates

import * as THREE from 'three';

export type FloatingTextType =
  | 'damage'
  | 'damage_crit'
  | 'heal'
  | 'heal_crit'
  | 'xp'
  | 'gold'
  | 'miss'
  | 'dodge'
  | 'block'
  | 'immune'
  | 'absorb'
  | 'mana'
  | 'buff'
  | 'debuff'
  | 'combo';

export interface FloatingTextOptions {
  type: FloatingTextType;
  value: string | number;
  position: THREE.Vector3;
  damageType?: string;
  isCritical?: boolean;
  scale?: number;
}

interface FloatingTextInstance {
  element: HTMLElement;
  // PERF: Store position components directly to avoid Vector3 overhead
  posX: number;
  posY: number;
  posZ: number;
  velX: number;
  velY: number;
  velZ: number;
  lifetime: number;
  maxLifetime: number;
  gravity: number;
  active: boolean;
}

export class FloatingTextSystem {
  private container: HTMLElement;
  private camera: THREE.Camera;
  private instances: FloatingTextInstance[] = [];
  private maxInstances: number = 100;
  private activeCount: number = 0;

  // PERF: Reusable vector for world-to-screen projection
  private readonly _tempVec: THREE.Vector3 = new THREE.Vector3();

  // PERF: Cache window dimensions
  private windowWidth: number = window.innerWidth;
  private windowHeight: number = window.innerHeight;

  // Type configurations
  private typeConfigs: Record<FloatingTextType, {
    color: string;
    size: string;
    fontWeight: string;
    outline: string;
    animation?: string;
  }> = {
    damage: { color: '#ffffff', size: '18px', fontWeight: 'bold', outline: '2px #000' },
    damage_crit: { color: '#ffff00', size: '26px', fontWeight: 'bold', outline: '2px #ff0000', animation: 'crit' },
    heal: { color: '#00ff00', size: '18px', fontWeight: 'bold', outline: '2px #000' },
    heal_crit: { color: '#00ff88', size: '24px', fontWeight: 'bold', outline: '2px #005500', animation: 'crit' },
    xp: { color: '#aa88ff', size: '16px', fontWeight: 'normal', outline: '2px #000' },
    gold: { color: '#ffd700', size: '16px', fontWeight: 'normal', outline: '2px #000' },
    miss: { color: '#888888', size: '14px', fontWeight: 'normal', outline: '2px #000' },
    dodge: { color: '#aaaaaa', size: '14px', fontWeight: 'normal', outline: '2px #000' },
    block: { color: '#888888', size: '14px', fontWeight: 'normal', outline: '2px #000' },
    immune: { color: '#ffffff', size: '14px', fontWeight: 'bold', outline: '2px #000' },
    absorb: { color: '#8888ff', size: '16px', fontWeight: 'normal', outline: '2px #000' },
    mana: { color: '#4488ff', size: '16px', fontWeight: 'normal', outline: '2px #000' },
    buff: { color: '#88ff88', size: '14px', fontWeight: 'normal', outline: '2px #005500' },
    debuff: { color: '#ff8888', size: '14px', fontWeight: 'normal', outline: '2px #550000' },
    combo: { color: '#ff8800', size: '22px', fontWeight: 'bold', outline: '2px #000', animation: 'combo' },
  };

  // Damage type colors
  private damageTypeColors: Record<string, string> = {
    physical: '#ffffff',
    fire: '#ff6600',
    ice: '#00ccff',
    lightning: '#ffff00',
    arcane: '#cc44ff',
    holy: '#ffff88',
    shadow: '#aa00aa',
    nature: '#00ff00',
  };

  constructor(camera: THREE.Camera) {
    this.camera = camera;

    this.container = document.createElement('div');
    this.container.id = 'floating-text-container';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      overflow: hidden;
      z-index: 500;
      font-family: 'Impact', 'Arial Black', sans-serif;
    `;

    document.body.appendChild(this.container);

    // PERF: Pre-create all instance slots with pooled elements
    for (let i = 0; i < this.maxInstances; i++) {
      const element = this.createElement();
      element.style.display = 'none';
      this.container.appendChild(element);

      this.instances.push({
        element,
        posX: 0, posY: 0, posZ: 0,
        velX: 0, velY: 0, velZ: 0,
        lifetime: 0,
        maxLifetime: 0,
        gravity: 0,
        active: false,
      });
    }

    // PERF: Cache window size on resize
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    });
  }

  public setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.style.cssText = `
      position: absolute;
      white-space: nowrap;
      text-align: center;
      transform: translate(-50%, -50%);
      pointer-events: none;
      will-change: transform, opacity;
    `;
    return el;
  }

  private getInactiveInstance(): FloatingTextInstance | null {
    // PERF: Find first inactive instance instead of creating new ones
    for (let i = 0; i < this.maxInstances; i++) {
      if (!this.instances[i].active) {
        return this.instances[i];
      }
    }
    return null; // Pool exhausted
  }

  public spawn(options: FloatingTextOptions): void {
    const instance = this.getInactiveInstance();
    if (!instance) return; // Pool exhausted, skip this text

    const config = this.typeConfigs[options.type];
    let color = config.color;

    // Override color for damage types
    if ((options.type === 'damage' || options.type === 'damage_crit') && options.damageType) {
      color = this.damageTypeColors[options.damageType] || color;
    }

    // Format value
    let displayValue = String(options.value);
    if (options.type === 'xp') {
      displayValue = `+${options.value} XP`;
    } else if (options.type === 'gold') {
      displayValue = `+${options.value} Gold`;
    } else if (options.type === 'heal' || options.type === 'heal_crit') {
      displayValue = `+${options.value}`;
    } else if (options.type === 'mana') {
      displayValue = `+${options.value}`;
    } else if (options.type === 'damage_crit') {
      displayValue = `${options.value}!`;
    }

    // Apply styles
    const scale = options.scale || 1;
    const fontSize = parseFloat(config.size) * scale;
    const element = instance.element;

    element.style.color = color;
    element.style.fontSize = `${fontSize}px`;
    element.style.fontWeight = config.fontWeight;
    element.style.textShadow = `-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 ${config.outline}`;
    element.style.opacity = '1';
    element.style.display = 'block';
    element.textContent = displayValue;

    // Add animation class
    if (config.animation === 'crit') {
      element.style.animation = 'floatCrit 0.5s ease-out';
    } else if (config.animation === 'combo') {
      element.style.animation = 'floatCombo 0.8s ease-out';
    } else {
      element.style.animation = '';
    }

    // Random horizontal velocity
    const spread = 0.5;

    // Determine lifetime based on type
    let lifetime = 1.5;
    if (options.type === 'xp' || options.type === 'gold') {
      lifetime = 2.0;
    } else if (options.type === 'buff' || options.type === 'debuff') {
      lifetime = 1.0;
    }

    // PERF: Store position/velocity as primitives
    instance.posX = options.position.x;
    instance.posY = options.position.y;
    instance.posZ = options.position.z;
    instance.velX = (Math.random() - 0.5) * spread;
    instance.velY = 2 + Math.random() * 0.5;
    instance.velZ = (Math.random() - 0.5) * spread;
    instance.lifetime = lifetime;
    instance.maxLifetime = lifetime;
    instance.gravity = 3;
    instance.active = true;

    this.activeCount++;
  }

  public update(deltaTime: number): void {
    if (this.activeCount === 0) return; // Early exit if nothing to update

    for (let i = 0; i < this.maxInstances; i++) {
      const instance = this.instances[i];
      if (!instance.active) continue;

      // Update lifetime
      instance.lifetime -= deltaTime;

      if (instance.lifetime <= 0) {
        instance.active = false;
        instance.element.style.display = 'none';
        this.activeCount--;
        continue;
      }

      // Apply gravity
      instance.velY -= instance.gravity * deltaTime;

      // Update position
      instance.posX += instance.velX * deltaTime;
      instance.posY += instance.velY * deltaTime;
      instance.posZ += instance.velZ * deltaTime;

      // Project to screen - reuse vector
      this._tempVec.set(instance.posX, instance.posY, instance.posZ);
      this._tempVec.project(this.camera);

      // Check if behind camera
      if (this._tempVec.z > 1) {
        instance.element.style.display = 'none';
        continue;
      }

      const x = (this._tempVec.x * 0.5 + 0.5) * this.windowWidth;
      const y = (-this._tempVec.y * 0.5 + 0.5) * this.windowHeight;

      // Check if off screen
      if (x < -50 || x > this.windowWidth + 50 || y < -50 || y > this.windowHeight + 50) {
        instance.element.style.display = 'none';
        continue;
      }

      instance.element.style.display = 'block';
      instance.element.style.left = `${x}px`;
      instance.element.style.top = `${y}px`;

      // Fade out
      const fadeStart = 0.3;
      if (instance.lifetime < fadeStart) {
        instance.element.style.opacity = String(instance.lifetime / fadeStart);
      }

      // Scale up slightly at start
      const age = instance.maxLifetime - instance.lifetime;
      if (age < 0.1) {
        const scaleUp = 1 + (1 - age / 0.1) * 0.3;
        instance.element.style.transform = `translate(-50%, -50%) scale(${scaleUp})`;
      } else {
        instance.element.style.transform = 'translate(-50%, -50%)';
      }
    }
  }

  // Convenience methods
  public showDamage(value: number, position: THREE.Vector3, damageType?: string, isCritical: boolean = false): void {
    this.spawn({
      type: isCritical ? 'damage_crit' : 'damage',
      value,
      position,
      damageType,
      isCritical,
    });
  }

  public showHeal(value: number, position: THREE.Vector3, isCritical: boolean = false): void {
    this.spawn({
      type: isCritical ? 'heal_crit' : 'heal',
      value,
      position,
      isCritical,
    });
  }

  public showXP(value: number, position: THREE.Vector3): void {
    this.spawn({ type: 'xp', value, position });
  }

  public showGold(value: number, position: THREE.Vector3): void {
    this.spawn({ type: 'gold', value, position });
  }

  public showMiss(position: THREE.Vector3): void {
    this.spawn({ type: 'miss', value: 'Miss', position });
  }

  public showDodge(position: THREE.Vector3): void {
    this.spawn({ type: 'dodge', value: 'Dodge', position });
  }

  public showBlock(value: number, position: THREE.Vector3): void {
    this.spawn({ type: 'block', value: `Block ${value}`, position });
  }

  public showImmune(position: THREE.Vector3): void {
    this.spawn({ type: 'immune', value: 'Immune', position });
  }

  public showAbsorb(value: number, position: THREE.Vector3): void {
    this.spawn({ type: 'absorb', value: `Absorb ${value}`, position });
  }

  public showMana(value: number, position: THREE.Vector3): void {
    this.spawn({ type: 'mana', value, position });
  }

  public showBuff(name: string, position: THREE.Vector3): void {
    this.spawn({ type: 'buff', value: `+${name}`, position });
  }

  public showDebuff(name: string, position: THREE.Vector3): void {
    this.spawn({ type: 'debuff', value: `-${name}`, position });
  }

  public showCombo(count: number, position: THREE.Vector3): void {
    this.spawn({ type: 'combo', value: `${count}x Combo!`, position, scale: 1.2 });
  }

  public clear(): void {
    for (let i = 0; i < this.maxInstances; i++) {
      this.instances[i].active = false;
      this.instances[i].element.style.display = 'none';
    }
    this.activeCount = 0;
  }

  public dispose(): void {
    this.clear();
    this.container.remove();
  }
}

// Add CSS animations (only once)
if (!document.getElementById('floating-text-styles')) {
  const style = document.createElement('style');
  style.id = 'floating-text-styles';
  style.textContent = `
    @keyframes floatCrit {
      0% { transform: translate(-50%, -50%) scale(0.5); }
      30% { transform: translate(-50%, -50%) scale(1.3); }
      100% { transform: translate(-50%, -50%) scale(1); }
    }

    @keyframes floatCombo {
      0% { transform: translate(-50%, -50%) scale(0.8) rotate(-10deg); }
      25% { transform: translate(-50%, -50%) scale(1.2) rotate(5deg); }
      50% { transform: translate(-50%, -50%) scale(1.1) rotate(-3deg); }
      100% { transform: translate(-50%, -50%) scale(1) rotate(0deg); }
    }
  `;
  document.head.appendChild(style);
}

// Alias for backward compatibility
export { FloatingTextSystem as FloatingText };
